
import { useState, useEffect } from 'react';

interface DateTimeStepProps {
  data: {
    date?: string;
    time?: string;
  };
  updateData: (data: Partial<{ date: string; time: string }>) => void;
  nextStep: () => void;
  prevStep: () => void;
}

/**
 * Component that lets the user pick a date and a time slot.
 * It fetches available slots from a Supabase edge function.
 */
export default function DateTimeStep({
  data,
  updateData,
  nextStep,
  prevStep,
}: DateTimeStepProps) {
  const [slots, setSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(true);
  const [message, setMessage] = useState('');
  const [totalReservations, setTotalReservations] = useState(0);

  /* -------------------------------------------------------------
   * Load slots whenever a new date is chosen
   * ------------------------------------------------------------- */
  useEffect(() => {
    if (data.date) {
      loadSlots();
    }
    // Reset time when the date changes
    updateData({ time: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.date]);

  const loadSlots = async () => {
    setLoading(true);
    setSlots([]);
    setAvailable(true);
    setMessage('');
    setTotalReservations(0);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-available-slots`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ date: data.date }),
        },
      );

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(
          `Failed to fetch slots (status ${response.status}): ${errorBody}`,
        );
      }

      const result: {
        available: boolean;
        slots?: string[];
        reason?: string;
        totalReservations?: number;
      } = await response.json();

      if (result.available) {
        setSlots(result.slots ?? []);
        setAvailable(true);
        setMessage('');
        setTotalReservations(result.totalReservations ?? 0);
      } else {
        setSlots([]);
        setAvailable(false);
        setMessage(result.reason ?? 'Date non disponible');
        setTotalReservations(0);
      }
    } catch (err) {
      console.error('Error loading slots:', err);
      setSlots([]);
      setAvailable(false);
      setMessage(
        err instanceof Error
          ? err.message
          : 'Erreur lors du chargement des créneaux',
      );
      setTotalReservations(0);
    } finally {
      setLoading(false);
    }
  };

  const canContinue = !!data.date && !!data.time;

  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
      {/* Navigation */}
      <button
        onClick={prevStep}
        className="flex items-center text-primary hover:text-primary/80 transition-colors duration-300 mb-4"
      >
        <i className="ri-arrow-left-line mr-2"></i>
        Retour
      </button>

      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">
          Choisissez votre créneau
        </h2>
        <p className="text-gray-400">Sélectionnez une date puis un horaire disponible</p>
      </div>

      {/* Form */}
      <div className="space-y-6 mb-6">
        {/* Date picker */}
        <div className="bg-dark-bg border border-dark-border rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-primary/20 rounded-full">
              <i className="ri-calendar-line text-xl text-primary"></i>
            </div>
            <h3 className="text-lg font-bold text-white">Date souhaitée</h3>
          </div>
          <input
            type="date"
            value={data.date ?? ''}
            onChange={(e) => updateData({ date: e.target.value })}
            className="w-full p-4 bg-dark-card border border-dark-border text-white rounded-lg focus:outline-none focus:border-primary text-lg cursor-pointer"
            min={new Date().toISOString().split('T')[0]}
          />
        </div>

        {/* Slots section – only shown after a date is selected */}
        {data.date && (
          <div className="bg-dark-bg border border-dark-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 flex items-center justify-center bg-primary/20 rounded-full">
                  <i className="ri-time-line text-xl text-primary"></i>
                </div>
                <h3 className="text-lg font-bold text-white">
                  Créneaux disponibles
                </h3>
              </div>
              {!loading && available && slots.length > 0 && (
                <div className="flex items-center space-x-2 bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-2">
                  <i className="ri-checkbox-circle-line text-green-400"></i>
                  <span className="text-green-400 font-semibold text-sm">
                    {slots.length} créneau{slots.length > 1 ? 'x' : ''} disponible{slots.length > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>

            {/* Info sur les réservations existantes */}
            {!loading && available && totalReservations > 0 && (
              <div className="mb-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <i className="ri-information-line text-blue-400"></i>
                  <p className="text-blue-400 text-sm">
                    <strong>{totalReservations}</strong> réservation{totalReservations > 1 ? 's' : ''} déjà confirmée{totalReservations > 1 ? 's' : ''} ce jour-là
                  </p>
                </div>
              </div>
            )}

            {loading ? (
              // Loading state
              <div className="text-center py-8">
                <i className="ri-loader-4-line animate-spin text-3xl text-primary mb-3"></i>
                <p className="text-gray-400">Chargement des créneaux disponibles...</p>
              </div>
            ) : !available ? (
              // Date not available
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
                <i className="ri-calendar-close-line text-4xl text-red-400 mb-3"></i>
                <p className="text-red-400 font-semibold mb-2">
                  Date non disponible
                </p>
                <p className="text-gray-400 text-sm">{message}</p>
              </div>
            ) : slots.length === 0 ? (
              // No slots for the chosen date
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 text-center">
                <i className="ri-calendar-line text-4xl text-yellow-400 mb-3"></i>
                <p className="text-yellow-400 font-semibold mb-2">
                  Aucun créneau disponible
                </p>
                <p className="text-gray-400 text-sm">
                  Tous les créneaux sont déjà réservés. Veuillez choisir une autre date.
                </p>
              </div>
            ) : (
              // List of slots
              <>
                <div className="mb-4 bg-primary/10 border border-primary/30 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <i className="ri-lightbulb-line text-primary mt-0.5"></i>
                    <div>
                      <p className="text-primary font-semibold text-sm mb-1">
                        Comment ça marche ?
                      </p>
                      <p className="text-gray-400 text-sm">
                        Les créneaux affichés ci-dessous sont <strong className="text-white">réellement disponibles</strong> en temps réel. 
                        Les horaires déjà réservés ne sont pas affichés.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {slots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => updateData({ time: slot })}
                      className={`p-4 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        data.time === slot
                          ? 'bg-primary text-dark-bg shadow-lg scale-105'
                          : 'bg-dark-card border border-dark-border text-gray-300 hover:border-primary hover:scale-105'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <i
                          className={`ri-time-line text-xl mb-1 ${
                            data.time === slot ? 'text-dark-bg' : 'text-primary'
                          }`}
                        ></i>
                        <span className="text-sm">{slot}</span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-4 flex items-center justify-center space-x-2 text-gray-400 text-sm">
                  <i className="ri-shield-check-line text-green-400"></i>
                  <span>Disponibilité vérifiée en temps réel</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Continue button */}
      <button
        onClick={nextStep}
        disabled={!canContinue}
        className="w-full py-4 bg-primary text-dark-bg rounded-xl font-bold text-lg hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
      >
        Continuer →
      </button>
    </div>
  );
}
